import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DressItem } from '../../../../api/DressesAPI';
import { Autocomplete } from '../../../../components/Autocomplete/Autocomplete';
import ConfirmationPopup from '../../../../components/ConfirmationPopup/ConfirmationPopup';
import ElementActions from '../../../../components/ElementActions/ElementActions';
import { Icon } from '../../../../components/Icon/Icon';
import IconButton from '../../../../components/IconButton/IconButton';
import ListContainer from '../../../../components/ListContainer/ListContainer';
import ListElement from '../../../../components/ListElement/ListElement';
import PrimaryButton from '../../../../components/PrimaryButton/PrimaryButton';
import { useGetProductionDresses } from '../../../../hooks/useGetProductionDresses';
import deleteIcon from "../../../../assets/images/bin.svg";
import edit from "../../../../assets/images/pen.svg";
import YearCollection from '../../../../components/YearCollection/YearCollection';
import { Modal } from '../../../../components/Modal/Modal';
import DressForm from '../../../../components/DressForm/DressForm';
import { FormType } from '../../../../shared/form.type';
import DressItemElement from '../../../../components/DressItemElement/DressItemElement';
import Card from '../../../../components/Card/Card';
import { PermissionFlags } from '../../../../components/PermissionFlags/PermissionFlags';
import SimpleListElement from '../../../../components/SimpleListElement/SimpleListElement';
import NoDataCTA from '../../../../components/NoDataCTA/NoDataCTA';
import Title from '../../../../components/Title/Title';
import PageWrapper from '../../../PageWrapper/PageWrapper';

const EmptyDress = {
  name: '',
  year: 2023,
  tags: [],
  materials: []
};

function DressesSection() {
  const { t } = useTranslation();
  const { isLoading, yearCollections, searchDresses, addDress, updateDress, removeDress } = useGetProductionDresses();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [editDressModalOpen, setEditDressModalOpen] = useState(false);
  const [currentDress, setCurrentDress] = useState<any>(null);
  const [action, setAction] = useState<FormType>('READ');

  const actionButton = (action: FormType) => {
    switch (action) {
      case 'CREATE':
        return <span className="flex mt-3 justify-center"><PrimaryButton text={t("admin.collections.items.form.add")!} fn={() => doAndClose(addDress, currentDress)} /></span>;
      case 'UPDATE':
        return <span className="flex mt-3 justify-center"><PrimaryButton text={t("admin.collections.items.form.update")!} fn={() => doAndClose(updateDress, currentDress)} /></span>;
      default:
        return;
    }
  };

  const toggleExpansion = (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
    }
  };

  const search = async (input: string, resultsSize: number, setResultsFn: any) => {
    let items = []
    try {
      items = await searchDresses(input);
      const filteredItems = items.slice(0, resultsSize);
      setResultsFn(filteredItems);
    } catch (err) {
      console.error('Could not search dress');
    }
  };

  const handleDress = async (action: FormType, dress: DressItem) => {
    setAction(action);
    setCurrentDress(dress);
    setEditDressModalOpen(true);
  };

  const doAndClose = async (fn: any, dress: DressItem) => {
    fn(dress);
    setCurrentDress(null);
    setEditDressModalOpen(false);
  }

  return (
    <PageWrapper>
      <div className="space-y-5">
        <Title text={t("dresses.header")} />
        <Card>
            <>
            <div className="container flex flex-wrap space-x-5">
              <Autocomplete
                resultsSize={15}
                searchFn={search}
                onSelect={(e: any) => handleDress('UPDATE', e)}
                placeholder={t('dresses.search.placeholder')!}
                isLoading={isLoading}
                errorMessage={t('global.empty_results')!}
                toElementFn={(item: DressItem) => 
                  <SimpleListElement onClick={(e) => {e.stopPropagation(); handleDress('READ', item); }} key={item.id} actions={
                    <>
                      <PermissionFlags requiredFlags={['update-dresses'] || []}>
                        <IconButton onClick={() => handleDress('UPDATE', item)}>
                          <Icon url={edit} cssStyles="w-5"/>
                        </IconButton>
                      </PermissionFlags>
                    </>
                    }>
                    <p>{ item.name }</p>
                  </SimpleListElement>
                }
              />
            </div>
            <div className="container flex flex-wrap space-x-5 my-3">
              <PermissionFlags requiredFlags={['create-dresses'] || []}>
                <PrimaryButton text={t("dresses.new_dress.button")!} fn={() => handleDress('CREATE', EmptyDress)}/>
              </PermissionFlags>
            </div>
          </>
        </Card>

        <Card>
          <>
            <ListContainer
              isLoading={isLoading}
              elements={yearCollections}
              emptyListComponent={
                <NoDataCTA
                  headline={t("dresses.empty.cta.title")}
                  subtext={t("dresses.empty.cta.body")}
                  primaryActionText={t("dresses.new_dress.button")}
                  onPrimaryAction={() => handleDress('CREATE', EmptyDress)}
                />
              }
              toElementFn={(yearCollection) => (
                <ListElement key={yearCollection.id} css="bg-white text-black" onClick={() => toggleExpansion(yearCollection.id)}>
                  <div className="flex flex-col w-full">
                    <YearCollection yearCollection={yearCollection} />
                    { !!expandedId && expandedId === yearCollection.id ?
                      <ListContainer
                      elements={yearCollection.dresses}
                      toElementFn={(dress: DressItem) => (
                          <ListElement key={dress.id} css="w-full flex duration-500 hover:bg-darker-gold-100" onClick={(e) => {e.stopPropagation(); handleDress('READ', dress); }}>
                            <>
                              <ElementActions actions={
                                <>
                                  <PermissionFlags requiredFlags={['update-dresses'] || []}>
                                    <IconButton onClick={() => handleDress('UPDATE', dress)}>
                                      <Icon url={edit} cssStyles="w-5"/>
                                    </IconButton>
                                  </PermissionFlags>
                                  <PermissionFlags requiredFlags={['remove-dresses'] || []}>
                                    <IconButton>
                                      <ConfirmationPopup onConfirm={() => removeDress(dress.id!)} confirmText={"global.confirm"} title={`${t("global.remove")}: ${dress.name}`}>
                                        <Icon url={deleteIcon} cssStyles="w-5"/>
                                      </ConfirmationPopup>
                                    </IconButton>
                                  </PermissionFlags>
                                </>
                              }>
                                <DressItemElement item={dress} />
                              </ElementActions>
                            </>
                          </ListElement>
                      )}
                      /> 
                      : null }
                  </div>
                </ListElement>
              )}
            />
          </>
        </Card>
        {editDressModalOpen && (
          <Modal
            modalTitle={currentDress.name || "dresses.form.new_dress"}
            canClose
            onClose={() => setEditDressModalOpen(false)}
          >
            <DressForm 
              dress={currentDress} 
              onChange={setCurrentDress} 
              isDisabled={action === 'READ'} 
              actionButton={actionButton(action)}
            />
          </Modal>
        )}
      </div>
    </PageWrapper>
  );
}

export default DressesSection;
